import { useState } from "react";
import qrcode from "qrcode-generator";
import { EnTetePage, Etiquette, Message } from "@cabinet/charte";
import { api, ErreurApi } from "../api/client";
import { useSession } from "../session";

const LIBELLES: Record<string, string> = {
  VALIDER_ECRITURE: "valider des écritures",
  CLOTURER_EXERCICE: "clôturer une période ou un exercice",
  DEPOSER_DECLARATION: "déposer une déclaration",
};

/**
 * Profil : le second facteur d'authentification (EF-0103).
 *
 * L'enrôlement se fait en deux temps : l'application d'authentification lit le
 * code QR, puis un premier code prouve qu'elle connaît le secret. Aussitôt
 * activé, la session en cours en bénéficie ; les connexions suivantes le
 * demanderont.
 */
export function PageProfil() {
  const { session, actualiser } = useSession();
  const [enrolement, setEnrolement] = useState<{ secret: string; adresse: string } | null>(null);
  const [code, setCode] = useState("");
  const [retour, setRetour] = useState<{ type: "succes" | "erreur"; texte: string } | null>(null);

  if (session == null) return null;

  async function enroler() {
    setRetour(null);
    try {
      setEnrolement(await api.enrolerLeSecondFacteur());
    } catch (echec) {
      setRetour({ type: "erreur", texte: echec instanceof ErreurApi ? echec.message : "L'enrôlement n'a pas abouti." });
    }
  }

  async function activer() {
    setRetour(null);
    try {
      actualiser(await api.activerLeSecondFacteur(code));
      setEnrolement(null);
      setCode("");
      setRetour({ type: "succes", texte: "Second facteur activé. Les prochaines connexions demanderont votre code." });
    } catch (echec) {
      setRetour({ type: "erreur", texte: echec instanceof ErreurApi ? echec.message : "L'activation n'a pas abouti." });
    }
  }

  const qr = enrolement != null ? codeQr(enrolement.adresse) : null;

  return (
    <>
      <EnTetePage titre="Profil" surTitre={session.utilisateur.courriel} description={session.utilisateur.nom} />

      {retour != null && <Message type={retour.type}>{retour.texte}</Message>}

      <div className="carte">
        <h2>
          Authentification à deux facteurs{" "}
          {session.secondFacteurActif ? (
            <Etiquette variante="succes">Active</Etiquette>
          ) : (
            <Etiquette variante="avertissement">Inactive</Etiquette>
          )}
        </h2>
        <p className="discret-texte">
          Un code à six chiffres, renouvelé toutes les trente secondes par une application d'authentification — Google
          Authenticator, Microsoft Authenticator, FreeOTP… —, s'ajoute au mot de passe. Il est exigé pour valider,
          clôturer et déposer une déclaration.
        </p>

        {session.permissionsRetenues.length > 0 && (
          <Message type="avertissement" titre="Droits retenus">
            Sans second facteur, cette session ne peut pas{" "}
            {session.permissionsRetenues.map((p) => LIBELLES[p] ?? p).join(", ")}.
          </Message>
        )}

        {session.secondFacteurActif ? (
          <p>
            {session.secondFacteurVerifie
              ? "Cette session a été ouverte avec votre code."
              : "Cette session a été ouverte sans votre code : se reconnecter pour en bénéficier."}{" "}
            Pour changer de téléphone, demander la réinitialisation à un administrateur du tenant.
          </p>
        ) : enrolement == null ? (
          <button type="button" className="principal" onClick={() => void enroler()}>
            Activer l'authentification à deux facteurs
          </button>
        ) : (
          <div className="enrolement">
            {qr != null && <div className="code-qr" dangerouslySetInnerHTML={{ __html: qr }} />}
            <div>
              <p>1. Scanner ce code avec l'application d'authentification.</p>
              <p className="discret-texte">
                À défaut, saisir la clé : <code>{enrolement.secret.replace(/(.{4})/g, "$1 ").trim()}</code>
              </p>
              <p>2. Saisir le code qu'elle affiche.</p>
              <form
                className="champs-en-ligne"
                onSubmit={(e) => {
                  e.preventDefault();
                  void activer();
                }}
              >
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={7}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9 ]/g, ""))}
                  placeholder="123 456"
                  autoFocus
                />
                <button type="submit" className="principal">
                  Activer
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/** Code QR en SVG ; la bibliothèque le produit elle-même, sans rien charger de l'extérieur. */
function codeQr(texte: string): string {
  const qr = qrcode(0, "M");
  qr.addData(texte);
  qr.make();
  return qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
}
